import React, { useCallback, useEffect, useRef, useState } from 'react';

import { tv } from '../../tv';

/**
 * A region that scrolls, and says so.
 *
 * Overflow alone is invisible: a list that happens to end at the fold and a
 * list with forty more rows below it look identical, which is how content
 * gets missed. The edge shadows are the affordance, appearing only on an
 * edge with more content past it and vanishing when the scroll reaches it.
 *
 * The shadows are driven by a scroll listener rather than CSS scroll-driven
 * animations, deliberately. The CSS route (a mask animated on a scroll
 * timeline) needs `animation-timeline: scroll()`, which has no Tailwind
 * utility, so it cannot be written as literal emitted ck: classes; hiding it
 * in the generator would put component styling where no component review
 * looks. JS in component internals is sanctioned, so the listener owns the
 * measurement (rAF-throttled, the Polaris approach) and emitted utilities
 * still own every pixel that CSS can express.
 *
 * Scrollbars stay native. `color-scheme` already renders them correctly per
 * theme, and restyled scrollbars are a hit target regression the reference
 * systems have been walking back.
 */

const scrollAreaStyles = tv({
  slots: {
    /* The wrapper exists so the shadows have a non-scrolling box to pin to;
     * overlays inside the scroller would ride away with the content. */
    root: 'ck:relative',
    viewport: 'ck:size-full',
    /* Shadows are a gradient from the neutral ALPHA scale, so they read as a
     * dimming of whatever surface they sit on, in both modes, rather than a
     * grey that only works on white. pointer-events-none because they float
     * over content that must stay clickable at the edge. */
    shadowTop:
      'ck:pointer-events-none ck:absolute ck:inset-x-0 ck:top-0 ck:h-3 ck:bg-linear-to-b ck:from-neutral-a-5',
    shadowBottom:
      'ck:pointer-events-none ck:absolute ck:inset-x-0 ck:bottom-0 ck:h-3 ck:bg-linear-to-t ck:from-neutral-a-5',
    shadowStart:
      'ck:pointer-events-none ck:absolute ck:inset-y-0 ck:left-0 ck:w-3 ck:bg-linear-to-r ck:from-neutral-a-5',
    shadowEnd:
      'ck:pointer-events-none ck:absolute ck:inset-y-0 ck:right-0 ck:w-3 ck:bg-linear-to-l ck:from-neutral-a-5',
  },
  variants: {
    /* The off axis is explicitly hidden: overflow-y-auto alone leaves
     * overflow-x visible, so a stray wide row would still scroll a
     * "vertical" area sideways. */
    direction: {
      vertical: { viewport: 'ck:overflow-y-auto ck:overflow-x-hidden' },
      horizontal: { viewport: 'ck:overflow-x-auto ck:overflow-y-hidden' },
      both: { viewport: 'ck:overflow-auto' },
    },
    /* Same contract as Stack's isFilled: take the space the parent
     * allocated. The min sizes are the load-bearing half: a flex child's
     * min size defaults to its content, so without min-h-0/min-w-0 the
     * scroll area grows with its content and never scrolls at all, which is
     * the single most common way a scroll container silently fails. */
    filled: {
      true: { root: 'ck:size-full ck:min-h-0 ck:min-w-0' },
      false: {},
    },
  },
  defaultVariants: { direction: 'vertical', filled: false },
});

type Edges = { top: boolean; bottom: boolean; start: boolean; end: boolean };

const noEdges: Edges = { top: false, bottom: false, start: false, end: false };

export interface ScrollAreaProps {
  children?: React.ReactNode;
  /** Which way the content overflows. Vertical is the common case. */
  direction?: 'vertical' | 'horizontal' | 'both';
  /**
   * Fills the space the parent allocated instead of hugging the content,
   * including the flex minimum-size handling without which a flex parent
   * cannot constrain a scroll container at all.
   */
  isFilled?: boolean;
  /**
   * Edge shadows hint at content past the fold. On by default because an
   * unsignalled overflow is the failure this component exists to prevent;
   * off for surfaces that carry their own boundary, such as a chat log whose
   * composer already sits on a rule.
   */
  hasEdgeShadows?: boolean;
  id?: string;
}

export const ScrollArea = React.forwardRef<HTMLDivElement, ScrollAreaProps>(function ScrollArea(
  { children, direction = 'vertical', isFilled = false, hasEdgeShadows = true, ...props },
  ref,
) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState<Edges>(noEdges);
  /* pending guards, frame cancels. Two refs because a synchronous rAF (test
   * environments, and nothing in the spec forbids it) would run the callback
   * before the handle was stored, wedging a single-ref guard closed. */
  const pending = useRef(false);
  const frame = useRef(0);

  const measure = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    /* Sub-pixel scroll positions mean the end of a scroll can rest a
     * fraction short of the limit; without the tolerance the far shadow
     * never quite goes out. */
    const fuzz = 1;
    const next: Edges = {
      top: el.scrollTop > fuzz,
      bottom: el.scrollTop + el.clientHeight < el.scrollHeight - fuzz,
      start: el.scrollLeft > fuzz,
      end: el.scrollLeft + el.clientWidth < el.scrollWidth - fuzz,
    };
    setEdges((prev) =>
      prev.top === next.top &&
      prev.bottom === next.bottom &&
      prev.start === next.start &&
      prev.end === next.end
        ? prev
        : next,
    );
  }, []);

  /* Scroll events outpace paint; measuring more than once per frame buys
   * nothing and costs layout reads on the hot path. */
  const scheduleMeasure = useCallback(() => {
    if (pending.current) return;
    pending.current = true;
    frame.current = requestAnimationFrame(() => {
      pending.current = false;
      measure();
    });
  }, [measure]);

  useEffect(() => {
    measure();
    const el = viewportRef.current;
    /* A resize changes what fits without any scroll event firing. Guarded:
     * jsdom has no ResizeObserver, and the scroll listener still covers
     * every interactive path without it. */
    if (typeof ResizeObserver === 'undefined' || !el) return undefined;
    const resizes = new ResizeObserver(scheduleMeasure);
    /* The children are observed alongside the viewport: content can grow
     * without the viewport resizing (rows appended to a list, an image
     * loading in), which changes scrollHeight while the viewport's own box
     * stays put, and a viewport-only observer would leave the shadows
     * stale. */
    const observe = () => {
      resizes.disconnect();
      resizes.observe(el);
      for (const child of el.children) resizes.observe(child);
    };
    observe();
    /* Children coming and going need the observation set rebuilt, and are
     * themselves a content change worth measuring: an added row moves
     * scrollHeight before any resize is observed. */
    const mutations = new MutationObserver(() => {
      observe();
      scheduleMeasure();
    });
    mutations.observe(el, { childList: true });
    return () => {
      mutations.disconnect();
      resizes.disconnect();
      cancelAnimationFrame(frame.current);
    };
  }, [measure, scheduleMeasure]);

  const { root, viewport, shadowTop, shadowBottom, shadowStart, shadowEnd } = scrollAreaStyles({
    direction,
    filled: isFilled,
  });

  const vertical = hasEdgeShadows && direction !== 'horizontal';
  const horizontal = hasEdgeShadows && direction !== 'vertical';

  return (
    <div {...props} className={root()} data-component="scroll-area">
      <div
        /* The ref callers get is the element that scrolls, not the wrapper:
         * the reason to hold a ref to a scroll area is to read or drive its
         * scroll position (a chat pinned to its latest message), and the
         * wrapper cannot do either. */
        ref={(el) => {
          viewportRef.current = el;
          if (typeof ref === 'function') ref(el);
          else if (ref) ref.current = el;
        }}
        className={viewport()}
        onScroll={scheduleMeasure}
      >
        {children}
      </div>
      {vertical && edges.top ? <div className={shadowTop()} aria-hidden /> : null}
      {vertical && edges.bottom ? <div className={shadowBottom()} aria-hidden /> : null}
      {horizontal && edges.start ? <div className={shadowStart()} aria-hidden /> : null}
      {horizontal && edges.end ? <div className={shadowEnd()} aria-hidden /> : null}
    </div>
  );
});
